// content/bosses/boss2.ts — BOSS 2: THE CISTERN KING (DESIGN §5, Zone 2).
//
//   "Verb: terrain control — the arena itself is the attack."
//
// A bloated drowned giant on a half-submerged throne, and four stone quadrants
// standing in a black moat. He never asks you to prioritise a target — there is
// nothing to prioritise, and that is the point: this fight is the zone's surge
// rhythm turned into a boss, and the only question in the room is where the
// floor will be in two seconds.
//
//   P1 (100-66%)  one quadrant at a time floods on a rotation you can learn,
//                 while brine-globs fall on marked ground. Rotate against it.
//   P2 (66-33%)   two OPPOSITE quadrants at once — half the safe ground gone —
//                 Mireskippers pour from two drains, and his slam throws a wave
//                 across the water. Dash through it, or stand on stone.
//   P3 (<33%)     he leaves the throne and wades after you, and the quadrant he
//                 stands in drowns for good. Safe ground shrinks to two, and the
//                 waves come faster.
//
// Every literal comes from data/zones/zone2.ts (ZONE2_BOSS). The numbers here
// are drawing geometry.

import type { Draw, RunCtx, Vec } from '../../types'
import { Boss, registerBoss } from '../../game/boss'
import { bossDefeated, registerBossArena } from '../../game/zones'
import { TILE } from '../../game/world'
import { ZONE2_BOSS, ZONE2_BOSS_ARENA, ZONE2_COLORS } from '../../data/zones/zone2'
import { arenaFx } from '../zones/zone2'
import { emit } from '../../engine/particles'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'

const D = ZONE2_BOSS
const C = ZONE2_COLORS

/** Per-phase array read, clamped — phases are 1-based. */
function byPhase<T>(arr: readonly T[], phase: number): T {
  return arr[Math.max(0, Math.min(arr.length - 1, phase - 1))]
}

const posScratch: Vec = { x: 0, y: 0 }

// --------------------------------------------------------------- brine globs
// FLAGGED (see ZONE2_BOSS in data/zones/zone2.ts): hand-rolled rather than
// pushed through the shared projectile pool, because RunCtx.spawnProjectile
// cannot aim a lobbed shot — it fixes the impact point 220px along the fired
// direction. Hand-rolling buys the landing marker, which is what makes a falling
// AoE fair. Cost: an Emberburst does not delete a glob in flight.

interface Glob {
  on: boolean
  x0: number
  y0: number
  tx: number
  ty: number
  t: number
  dur: number
}

type QuadFloodPhase = 'gap' | 'telegraph' | 'flooded'

class CisternKing extends Boss {
  readonly id = D.id
  /** read structurally by game/hud.ts for the boss bar */
  readonly name = D.name
  readonly phaseTicks = [D.phase3At, D.phase2At]

  private built = false
  private anim = 0

  // --- the quadrant flood
  private floodPhase: QuadFloodPhase = 'gap'
  private floodT = 0
  private active: number[] = []
  private rotCursor = 0
  private pairCursor = 0
  private permanent: boolean[] = [false, false, false, false]
  private floodDmgAcc = 0
  private floodPushAcc = 0

  // --- brine globs
  private globs: Glob[] = []
  private globT = 0
  private globWind = 0

  // --- the slam wave
  private waveT = 0
  private waveWind = 0
  private waveR = -1
  private waveHit = false

  // --- the drains
  private pourT = 0
  private drainCursor = 0

  // --- P3
  private standing = false
  private claimT = 0
  private claimQuad = -1

  private dying = false
  private tookDamage = false
  private lastHp = -1
  private hurtT = 0

  constructor() {
    super(D.hp)
    this.radius = D.radius
    this.pos.x = D.thronePos.x
    this.pos.y = D.thronePos.y
    for (let i = 0; i < 14; i++) this.globs.push({ on: false, x0: 0, y0: 0, tx: 0, ty: 0, t: 0, dur: 1 })
  }

  // ------------------------------------------------------------------ build

  private build(ctx: RunCtx): void {
    this.built = true
    arenaFx.active = true
    arenaFx.quads.length = 0
    for (const q of D.quadrants) {
      arenaFx.quads.push({
        state: 0,
        x: q.x0 * TILE,
        y: q.y0 * TILE,
        w: (q.x1 - q.x0 + 1) * TILE,
        h: (q.y1 - q.y0 + 1) * TILE,
      })
    }
    arenaFx.wave.on = false
    this.floodPhase = 'gap'
    this.floodT = byPhase(D.flood.gap, 1)
    this.globT = byPhase(D.glob.interval, 1) * 0.7
    this.waveT = 0
    this.pourT = 0
    this.lastHp = ctx.player.hp
    ctx.bark('THE CISTERN KING. He does not chase you — he takes the floor.')
    ctx.sfx('door')
    ctx.shake(8, 0.9)
  }

  // ------------------------------------------------------------------ phases

  protected onPhase(phase: number): void {
    const ctx = this.ctx
    if (!ctx) return
    if (phase === 2) {
      ctx.bark('He drowns two at once now. Watch which two.')
      this.waveT = byPhase(D.wave.interval, 2) * 0.5
      this.pourT = byPhase(D.pour.interval, 2) * 0.5
    } else if (phase === 3) {
      this.standing = true
      this.claimT = 0
      this.claimQuad = -1
      ctx.bark('He stands. Whatever he wades to is gone for good.')
      this.waveT = Math.min(this.waveT, byPhase(D.wave.interval, 3))
    }
    ctx.shake(6, 0.6)
    ctx.sfx('telegraph')
  }

  private applyPhase(): void {
    const f = this.healthFraction
    this.setPhase(f <= D.phase3At ? 3 : f <= D.phase2At ? 2 : 1)
  }

  takeDamage(n: number): void {
    if (!this.alive || n <= 0) return
    const before = this.hp
    super.takeDamage(n)
    if (this.hp < before) this.hurtT = 0.12
    // phase turns wherever health turns, not on the next tick (boss1's lesson:
    // damage arrives from several places inside one step)
    this.applyPhase()
  }

  // ------------------------------------------------------------------ update

  update(ctx: RunCtx, dt: number): void {
    this.tick(ctx, dt)
    if (!this.built) this.build(ctx)
    this.anim += dt
    if (this.hurtT > 0) this.hurtT -= dt

    if (this.dying) return
    if (!this.alive) {
      this.die(ctx)
      return
    }

    // no-hit watch, same reading as boss 1: any drop in the Warden's health
    // during the fight spends the clean kill. Under ?invuln=1 nothing reaches
    // the player, so an invulnerable run reads as untouched — true, and noted
    // where the test asserts it.
    if (this.lastHp >= 0 && ctx.player.hp < this.lastHp) this.tookDamage = true
    this.lastHp = ctx.player.hp

    this.applyPhase()
    const phase = this.phase

    if (this.standing) this.wade(ctx, dt)
    this.updateFlood(ctx, dt, phase)
    this.updateGlobs(ctx, dt, phase)
    this.updateWave(ctx, dt, phase)
    this.updatePour(ctx, dt, phase)
    this.publishFx()
  }

  // ------------------------------------------------------------- the quadrants

  private quadOf(x: number, y: number): number {
    const tx = Math.floor(x / TILE)
    const ty = Math.floor(y / TILE)
    for (let i = 0; i < D.quadrants.length; i++) {
      const q = D.quadrants[i]
      if (tx >= q.x0 && tx <= q.x1 && ty >= q.y0 && ty <= q.y1) return i
    }
    return -1
  }

  private quadCenter(i: number, out: Vec): void {
    const q = D.quadrants[i]
    out.x = ((q.x0 + q.x1 + 1) / 2) * TILE
    out.y = ((q.y0 + q.y1 + 1) / 2) * TILE
  }

  /** Everything the drowned floor does, in one clock. */
  private updateFlood(ctx: RunCtx, dt: number, phase: number): void {
    this.floodT -= dt

    if (this.floodPhase === 'gap') {
      if (this.floodT <= 0) {
        this.chooseQuadrants(phase)
        this.floodPhase = 'telegraph'
        this.floodT = byPhase(D.flood.telegraph, phase)
        this.telegraph('quadrant-flood', this.floodT)
        ctx.sfx('door')
        ctx.shake(2, 0.4)
      }
      return
    }

    if (this.floodPhase === 'telegraph') {
      if (this.floodT <= 0) {
        this.floodPhase = 'flooded'
        this.floodT = byPhase(D.flood.duration, phase)
        this.floodDmgAcc = 0
        this.floodPushAcc = 0
        ctx.sfx('generator-die')
        ctx.shake(9, 0.5)
        ctx.hitstop(0.06)
        for (const i of this.active) {
          this.quadCenter(i, posScratch)
          emit(posScratch.x, posScratch.y, {
            count: 26,
            color: C.floodHot,
            speed: 300,
            speedVar: 220,
            life: 0.6,
            lifeVar: 0.3,
            size: 5,
            grav: 320,
            shape: 1,
          })
        }
      }
      return
    }

    // flooded
    this.hurtFloor(ctx, dt)
    if (this.floodT <= 0) {
      this.floodPhase = 'gap'
      this.floodT = byPhase(D.flood.gap, phase)
      this.active.length = 0
      ctx.sfx('door')
    }
  }

  /** P1 rotates one; P2 takes an opposite pair; P3 keeps the pair and adds his own. */
  private chooseQuadrants(phase: number): void {
    this.active.length = 0
    if (phase === 1) {
      this.active.push(D.rotation[this.rotCursor % D.rotation.length])
      this.rotCursor++
      return
    }
    const pair = D.oppositePairs[this.pairCursor % D.oppositePairs.length]
    this.pairCursor++
    this.active.push(pair[0], pair[1])
  }

  /** Standing on drowned stone: 15 dmg/s and shoved away from the slam. */
  private hurtFloor(ctx: RunCtx, dt: number): void {
    const q = this.quadOf(ctx.player.pos.x, ctx.player.pos.y)
    const drowned = q >= 0 && (this.permanent[q] || this.active.indexOf(q) >= 0)
    this.floodDmgAcc += dt
    this.floodPushAcc += dt
    if (!drowned) {
      this.floodDmgAcc = Math.min(this.floodDmgAcc, D.flood.damageTickSeconds)
      this.floodPushAcc = Math.min(this.floodPushAcc, D.flood.pushTickSeconds)
      return
    }
    if (this.floodDmgAcc >= D.flood.damageTickSeconds) {
      this.floodDmgAcc = 0
      ctx.damagePlayer(D.flood.damagePerSecond * D.flood.damageTickSeconds, 'flood')
    }
    if (this.floodPushAcc >= D.flood.pushTickSeconds) {
      this.floodPushAcc = 0
      const dx = ctx.player.pos.x - this.pos.x
      const dy = ctx.player.pos.y - this.pos.y
      const l = Math.hypot(dx, dy) || 1
      ctx.pushPlayer((dx / l) * D.flood.push, (dy / l) * D.flood.push)
    }
  }

  /** Anything the permanent floods took stays taken; the last two never go. */
  private wade(ctx: RunCtx, dt: number): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const l = Math.hypot(dx, dy) || 1
    if (l > this.radius + 18) {
      this.pos.x += (dx / l) * D.wade.speed * dt
      this.pos.y += (dy / l) * D.wade.speed * dt
    }

    let claimed = 0
    for (const p of this.permanent) if (p) claimed++
    const q = this.quadOf(this.pos.x, this.pos.y)
    if (q < 0 || this.permanent[q] || claimed >= D.wade.permanentCap) {
      this.claimQuad = -1
      this.claimT = 0
      return
    }
    if (q !== this.claimQuad) {
      this.claimQuad = q
      this.claimT = 0
    }
    this.claimT += dt
    if (this.claimT >= D.wade.claimSeconds) {
      this.permanent[q] = true
      this.claimQuad = -1
      this.claimT = 0
      const ctx2 = this.ctx
      this.quadCenter(q, posScratch)
      emit(posScratch.x, posScratch.y, {
        count: 34,
        color: C.flood,
        speed: 260,
        speedVar: 200,
        life: 0.8,
        lifeVar: 0.3,
        size: 5,
        grav: 260,
        shape: 1,
      })
      ctx2?.shake(10, 0.7)
      ctx2?.sfx('generator-die')
      ctx2?.bark(`${D.quadrants[q].name} is gone. Two left.`)
    }
  }

  // --------------------------------------------------------------- brine globs

  private updateGlobs(ctx: RunCtx, dt: number, phase: number): void {
    for (const g of this.globs) {
      if (!g.on) continue
      g.t += dt
      if (g.t >= g.dur) {
        g.on = false
        this.globLand(ctx, g)
      }
    }

    if (this.globWind > 0) {
      this.globWind -= dt
      if (this.globWind <= 0) this.throwGlobs(ctx, phase)
      return
    }
    this.globT -= dt
    if (this.globT > 0) return
    this.globT = byPhase(D.glob.interval, phase)
    this.globWind = D.glob.windup
    this.telegraph('brine-glob', D.glob.windup)
  }

  private throwGlobs(ctx: RunCtx, phase: number): void {
    const n = byPhase(D.glob.count, phase)
    let thrown = 0
    for (const g of this.globs) {
      if (thrown >= n) break
      if (g.on) continue
      g.on = true
      g.x0 = this.pos.x
      g.y0 = this.pos.y - 16
      // scattered around the Warden, so a standing target still has to move but
      // a moving one is not punished for moving
      const a = ctx.rng() * Math.PI * 2
      const r = ctx.rng() * D.glob.scatter
      g.tx = ctx.player.pos.x + Math.cos(a) * r
      g.ty = ctx.player.pos.y + Math.sin(a) * r
      g.t = 0
      g.dur = D.glob.flightSeconds
      thrown++
    }
    if (thrown > 0) {
      ctx.sfx('shoot')
      emit(this.pos.x, this.pos.y - 10, {
        count: 12,
        color: D.glob.color,
        speed: 170,
        life: 0.3,
        size: 3,
        shape: 1,
      })
    }
  }

  private globLand(ctx: RunCtx, g: Glob): void {
    emit(g.tx, g.ty, {
      count: 22,
      color: D.glob.color,
      speed: 210,
      speedVar: 150,
      life: 0.45,
      lifeVar: 0.2,
      size: 4,
      grav: 220,
      shape: 1,
    })
    ctx.shake(2.4, 0.18)
    ctx.sfx('hit')
    const dx = ctx.player.pos.x - g.tx
    const dy = ctx.player.pos.y - g.ty
    if (dx * dx + dy * dy <= D.glob.splash * D.glob.splash) {
      ctx.damagePlayer(D.glob.damage, 'brine-glob')
    }
  }

  // ---------------------------------------------------------------- the wave

  private updateWave(ctx: RunCtx, dt: number, phase: number): void {
    const interval = byPhase(D.wave.interval, phase)

    if (this.waveR >= 0) {
      this.waveR += D.wave.speed * dt
      arenaFx.wave.on = true
      arenaFx.wave.x = this.pos.x
      arenaFx.wave.y = this.pos.y
      arenaFx.wave.r = this.waveR
      arenaFx.wave.thickness = D.wave.thickness
      if (!this.waveHit) this.waveTouch(ctx)
      if (this.waveR > D.wave.maxRadius) {
        this.waveR = -1
        arenaFx.wave.on = false
      }
      return
    }

    if (this.waveWind > 0) {
      this.waveWind -= dt
      if (this.waveWind <= 0) {
        this.waveR = this.radius
        this.waveHit = false
        ctx.sfx('generator-die')
        ctx.shake(11, 0.5)
        ctx.hitstop(0.07)
        emit(this.pos.x, this.pos.y + 12, {
          count: 30,
          color: C.floodHot,
          speed: 330,
          speedVar: 210,
          life: 0.5,
          lifeVar: 0.2,
          size: 4,
          grav: 280,
          shape: 1,
        })
      }
      return
    }

    if (interval <= 0) return
    this.waveT -= dt
    if (this.waveT > 0) return
    this.waveT = interval
    this.waveWind = D.wave.windup
    this.telegraph('slam', D.wave.windup)
    ctx.bark(phase >= 3 ? 'He slams again. They come quicker now.' : 'He raises both arms. Get on stone, or dash it.')
  }

  /**
   * The wave rides the water. Dry stone is cover, and a dash is a dodge — the
   * i-frame read is deliberate and matches every other damage path in the game
   * (game/player.ts hurt() refuses damage inside i-frames).
   */
  private waveTouch(ctx: RunCtx): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const d = Math.hypot(dx, dy)
    if (Math.abs(d - this.waveR) > D.wave.thickness * 0.5) return
    this.waveHit = true
    if (ctx.tileAt(ctx.player.pos) !== 'W') return
    const iframes = (ctx.player as unknown as { iframes?: number }).iframes ?? 0
    if (iframes > 0) return
    ctx.damagePlayer(D.wave.damage, 'surface-wave')
    ctx.pushPlayer((dx / (d || 1)) * 180, (dy / (d || 1)) * 180)
  }

  // ---------------------------------------------------------------- the drains

  private liveChildren(ctx: RunCtx): number {
    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) if (list[i].alive && list[i].tags.cisternking !== undefined) n++
    return n
  }

  private updatePour(ctx: RunCtx, dt: number, phase: number): void {
    const interval = byPhase(D.pour.interval, phase)
    if (interval <= 0) return
    this.pourT -= dt
    if (this.pourT > 0) return
    this.pourT = interval
    if (this.liveChildren(ctx) >= D.pour.liveCap) return

    const n = byPhase(D.pour.count, phase)
    for (let k = 0; k < n; k++) {
      if (this.liveChildren(ctx) >= D.pour.liveCap) return
      const drain = D.pour.drainTiles[this.drainCursor % D.pour.drainTiles.length]
      this.drainCursor++
      posScratch.x = drain.tx * TILE + TILE / 2 + (ctx.rng() - 0.5) * 20
      posScratch.y = drain.ty * TILE + TILE / 2 + (ctx.rng() - 0.5) * 40
      const e = ctx.spawnEnemy(D.pour.body, posScratch, { elite: null })
      if (!e.alive) continue
      e.tags.cisternking = 1
      emit(posScratch.x, posScratch.y, {
        count: 10,
        color: C.caustic,
        speed: 140,
        life: 0.35,
        size: 3,
        shape: 1,
      })
    }
    ctx.sfx('door')
  }

  /** Hand the floor state to the zone mechanic, which draws it under the fight. */
  private publishFx(): void {
    const tele = this.floodPhase === 'telegraph'
    const teleDur = byPhase(D.flood.telegraph, this.phase)
    const k = tele ? 1 - Math.max(0, this.floodT) / Math.max(0.01, teleDur) : 0
    for (let i = 0; i < arenaFx.quads.length; i++) {
      const q = arenaFx.quads[i]
      if (this.permanent[i]) {
        q.state = -2
        continue
      }
      const isActive = this.active.indexOf(i) >= 0
      if (!isActive) {
        q.state = 0
        continue
      }
      q.state = this.floodPhase === 'flooded' ? -1 : Math.max(0.001, k)
    }
  }

  // ------------------------------------------------------------------ death

  private die(ctx: RunCtx): void {
    this.dying = true
    arenaFx.active = false
    arenaFx.quads.length = 0
    arenaFx.wave.on = false
    // nothing it started may outlive it: globs frozen mid-arc and a half-claimed
    // quadrant would hang over the corpse for the whole celebration
    for (const g of this.globs) g.on = false
    this.claimQuad = -1
    ctx.hitstop(D.death.hitstop)
    ctx.shake(D.death.shake, D.death.shakeSeconds)
    ctx.sfx('generator-die')
    ctx.sfx('death')
    for (let i = 0; i < 3; i++) {
      emit(this.pos.x, this.pos.y, {
        count: 42,
        color: i === 0 ? C.floodHot : i === 1 ? C.flood : '#2a5a48',
        speed: 260 + i * 100,
        speedVar: 210,
        life: 1,
        lifeVar: 0.5,
        size: 5,
        grav: 200,
        drag: 0.88,
      })
    }
    const list = ctx.enemies
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i]
      if (e.alive) e.kill(true)
    }

    ctx.featTick('boss2_kill')
    if (!this.tookDamage) {
      ctx.featTick('boss2_nohit')
      ctx.bark('Dry feet. The tide never had you.')
    }
    ctx.bark('The Cistern King goes under. The water goes quiet.')
    bossDefeated(ctx)
  }

  // ----------------------------------------------------------------- render

  render(d: Draw): void {
    const ctx = d.ctx
    this.renderDrains(ctx)
    if (!this.standing) this.renderThrone(ctx)
    this.renderKing(ctx)
    this.renderWave(ctx)
    this.renderGlobs(ctx)
    this.renderTelegraphs(d)
    if (this.standing && this.claimQuad >= 0) this.renderClaim(ctx)
  }

  private renderDrains(ctx: CanvasRenderingContext2D): void {
    for (const t of D.pour.drainTiles) {
      drawSprite(ctx, 'z2_drain', t.tx * TILE + TILE / 2, t.ty * TILE + TILE / 2)
    }
  }

  /** The throne: a high-backed slab of drowned masonry he is fused into until P3. */
  private renderThrone(ctx: CanvasRenderingContext2D): void {
    const x = D.thronePos.x
    const y = D.thronePos.y
    ctx.save()
    // the back, rising behind him
    ctx.fillStyle = '#0e2028'
    ctx.fillRect(x - 56, y - 104, 112, 118)
    ctx.fillStyle = '#1e3843'
    ctx.fillRect(x - 48, y - 98, 96, 110)
    ctx.fillStyle = '#2d5361'
    ctx.fillRect(x - 48, y - 98, 96, 5)
    // a sunken relief in the back
    ctx.fillStyle = '#08161c'
    ctx.fillRect(x - 30, y - 88, 60, 62)
    ctx.strokeStyle = C.rootDim
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(x, y - 60, 20, 0, Math.PI * 2)
    ctx.stroke()
    // arms of the seat, low and wide
    ctx.fillStyle = '#1e3843'
    ctx.fillRect(x - 76, y - 24, 22, 46)
    ctx.fillRect(x + 54, y - 24, 22, 46)
    ctx.fillStyle = '#2d5361'
    ctx.fillRect(x - 76, y - 24, 22, 4)
    ctx.fillRect(x + 54, y - 24, 22, 4)
    // weed hanging off every edge
    ctx.fillStyle = C.rootDim
    for (let i = 0; i < 9; i++) {
      const px = x - 46 + i * 11
      ctx.fillRect(px, y - 98, 2, 9 + ((i * 7) % 13))
    }
    ctx.restore()
  }

  private renderKing(ctx: CanvasRenderingContext2D): void {
    const t = this.anim
    // slam pose: he compresses through the telegraph and hits bottom on the beat
    const winding = this.floodPhase === 'telegraph' || this.waveWind > 0
    const squash = winding ? 1 + 0.1 * Math.sin(t * 22) : 1
    const bob = Math.sin(t * 1.6) * 2

    ctx.save()
    ctx.globalAlpha = 0.4
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.ellipse(this.pos.x, this.pos.y + 44, 52, 15, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()

    // the water he stands in, always churning
    ctx.save()
    ctx.globalAlpha = 0.3 + 0.12 * Math.sin(t * 3)
    ctx.strokeStyle = C.caustic
    ctx.lineWidth = 2
    for (let i = 0; i < 3; i++) {
      const r = 34 + ((t * 34 + i * 22) % 66)
      ctx.globalAlpha = 0.28 * (1 - (r - 34) / 66)
      ctx.beginPath()
      ctx.ellipse(this.pos.x, this.pos.y + 40, r, r * 0.34, 0, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.restore()

    // a cold pool under him, so the body reads against the black moat without
    // a halo sitting on top of his face
    ctx.save()
    ctx.globalAlpha = 0.3 + 0.08 * Math.sin(t * 2.4)
    const glow = ctx.createRadialGradient(this.pos.x, this.pos.y + 20, 8, this.pos.x, this.pos.y + 20, 120)
    glow.addColorStop(0, 'rgba(92,224,255,0.45)')
    glow.addColorStop(1, 'rgba(92,224,255,0)')
    ctx.fillStyle = glow
    ctx.fillRect(this.pos.x - 120, this.pos.y - 100, 240, 240)
    ctx.restore()

    drawSprite(ctx, this.standing ? 'cistern_king_stand' : 'cistern_king', this.pos.x, this.pos.y + bob, {
      scale: 2.1 / squash,
      white: this.hurtT > 0 ? 0.85 : 0,
    })
  }

  private renderWave(ctx: CanvasRenderingContext2D): void {
    if (this.waveWind > 0) {
      const k = 1 - this.waveWind / D.wave.windup
      ctx.save()
      ctx.globalAlpha = 0.2 + 0.4 * k
      ctx.strokeStyle = C.floodHot
      ctx.lineWidth = 4 + k * 8
      ctx.beginPath()
      ctx.arc(this.pos.x, this.pos.y, this.radius + 18 + k * 22, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
      return
    }
    if (this.waveR < 0) return
    const fade = Math.max(0, 1 - this.waveR / D.wave.maxRadius)
    ctx.save()
    ctx.globalAlpha = 0.5 * fade + 0.25
    ctx.strokeStyle = C.flood
    ctx.lineWidth = D.wave.thickness
    ctx.beginPath()
    ctx.arc(this.pos.x, this.pos.y, this.waveR, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalAlpha = 0.85 * fade + 0.15
    ctx.strokeStyle = C.floodHot
    ctx.lineWidth = 5
    ctx.beginPath()
    ctx.arc(this.pos.x, this.pos.y, this.waveR, 0, Math.PI * 2)
    ctx.stroke()
    ctx.restore()
  }

  private renderGlobs(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    for (const g of this.globs) {
      if (!g.on) continue
      const k = Math.min(1, g.t / g.dur)
      const x = g.x0 + (g.tx - g.x0) * k
      const y = g.y0 + (g.ty - g.y0) * k
      const lift = Math.sin(k * Math.PI) * D.glob.arcHeight

      // the landing marker: this is the telegraph, and it is on the floor where
      // the damage will be
      ctx.globalAlpha = 0.22 + 0.4 * k
      ctx.strokeStyle = D.glob.color
      ctx.lineWidth = 2 + k * 2
      ctx.beginPath()
      ctx.arc(g.tx, g.ty, D.glob.splash, 0, Math.PI * 2)
      ctx.stroke()
      ctx.globalAlpha = 0.12 + 0.2 * k
      ctx.fillStyle = D.glob.color
      ctx.beginPath()
      ctx.arc(g.tx, g.ty, D.glob.splash * k, 0, Math.PI * 2)
      ctx.fill()

      // shadow on the water under the arc
      ctx.globalAlpha = 0.3
      ctx.fillStyle = '#000000'
      ctx.beginPath()
      ctx.ellipse(x, y, 9, 4, 0, 0, Math.PI * 2)
      ctx.fill()

      ctx.globalAlpha = 0.4
      ctx.fillStyle = D.glob.color
      ctx.beginPath()
      ctx.arc(x, y - lift, 13, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
      ctx.fillStyle = D.glob.color
      ctx.beginPath()
      ctx.arc(x, y - lift, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#e8ffd6'
      ctx.beginPath()
      ctx.arc(x - 2, y - lift - 2, 3, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }

  /** P3: the quadrant he is standing on, filling up. */
  private renderClaim(ctx: CanvasRenderingContext2D): void {
    const q = arenaFx.quads[this.claimQuad]
    if (!q) return
    const k = Math.min(1, this.claimT / D.wade.claimSeconds)
    ctx.save()
    ctx.globalAlpha = 0.25 + 0.35 * k
    ctx.strokeStyle = C.floodHot
    ctx.lineWidth = 3
    ctx.setLineDash([12, 8])
    ctx.strokeRect(q.x + 2, q.y + 2, q.w - 4, q.h - 4)
    ctx.setLineDash([])
    ctx.globalAlpha = 0.5
    ctx.fillStyle = C.flood
    ctx.fillRect(q.x, q.y + q.h - q.h * k, q.w, q.h * k * 0.12)
    ctx.restore()
  }
}

// ---------------------------------------------------------------- sprites
// A bloated drowned giant. Chunky, dark-outlined, and enormous — at 1.7x he is
// three times the width of anything else in the zone, which is the whole read.

registerPainter('cistern_king', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const stand = typeof p.stand === 'number' && p.stand > 0
  // sickly, waterlogged, rotting green-grey — never the pale mint of the roster
  const flesh = '#456156'
  const fleshLo = shade(flesh, -0.45)
  const fleshHi = shade(flesh, 0.28)
  const drowned = '#1d3232'
  const crown = '#8a6f33'
  const eye = '#9fe8ff'
  const top = stand ? 0.1 : 0.18

  // legs / submerged mass
  if (stand) {
    a.rect(Math.round(cx - s * 0.24), Math.round(s * 0.7), Math.round(s * 0.16), Math.round(s * 0.26), fleshLo)
    a.rect(Math.round(cx + s * 0.08), Math.round(s * 0.7), Math.round(s * 0.16), Math.round(s * 0.26), fleshLo)
    a.ellipse(cx - s * 0.16, s * 0.96, s * 0.13, s * 0.05, drowned)
    a.ellipse(cx + s * 0.16, s * 0.96, s * 0.13, s * 0.05, drowned)
  } else {
    a.ellipse(cx, s * 0.9, s * 0.42, s * 0.1, drowned)
  }

  // the belly: gross, waterlogged, the biggest shape on screen
  a.ellipse(cx, s * (top + 0.52), s * 0.42, s * 0.3, fleshLo)
  a.ellipse(cx, s * (top + 0.5), s * 0.38, s * 0.27, flesh)
  a.ellipse(cx, s * (top + 0.56), s * 0.26, s * 0.16, fleshHi)
  // rib-scars / bloat seams
  for (let i = 0; i < 3; i++) {
    a.hline(Math.round(cx - s * 0.2), Math.round(s * (top + 0.42 + i * 0.08)), Math.round(s * 0.4), fleshLo)
  }

  // arms: long, heavy, hanging into the water
  const armY = s * (top + 0.36)
  a.ellipse(cx - s * 0.44, armY + s * 0.06, s * 0.12, s * 0.2, fleshLo)
  a.ellipse(cx + s * 0.44, armY + s * 0.06, s * 0.12, s * 0.2, fleshLo)
  a.ellipse(cx - s * 0.46, armY + s * 0.26, s * 0.1, s * 0.09, flesh)
  a.ellipse(cx + s * 0.46, armY + s * 0.26, s * 0.1, s * 0.09, flesh)

  // shoulders and the sunk head
  a.ellipse(cx, s * (top + 0.3), s * 0.34, s * 0.14, flesh)
  a.ellipse(cx, s * (top + 0.16), s * 0.2, s * 0.17, flesh)
  a.ellipse(cx, s * (top + 0.22), s * 0.14, s * 0.09, fleshLo)
  // a slack, drowned mouth
  a.ellipse(cx, s * (top + 0.24), s * 0.08, s * 0.05, '#12222a')
  // two lamp-eyes, deep in the sockets
  a.ellipse(cx - s * 0.08, s * (top + 0.14), s * 0.045, s * 0.04, '#0b1a20')
  a.ellipse(cx + s * 0.08, s * (top + 0.14), s * 0.045, s * 0.04, '#0b1a20')
  a.set(Math.round(cx - s * 0.08), Math.round(s * (top + 0.14)), eye)
  a.set(Math.round(cx + s * 0.08), Math.round(s * (top + 0.14)), eye)
  a.set(Math.round(cx - s * 0.08), Math.round(s * (top + 0.145)), '#e8fbff')
  a.set(Math.round(cx + s * 0.08), Math.round(s * (top + 0.145)), '#e8fbff')

  // the crown: rusted iron spikes, half eaten by the water
  const cy = s * (top - 0.01)
  a.rect(Math.round(cx - s * 0.2), Math.round(cy), Math.round(s * 0.4), 4, crown)
  for (let i = -2; i <= 2; i++) {
    const x = Math.round(cx + i * s * 0.09)
    const h = 6 + (i === 0 ? 6 : Math.abs(i) === 1 ? 3 : 0)
    a.rect(x - 1, Math.round(cy) - h, 3, h, i % 2 === 0 ? crown : shade(crown, -0.3))
  }
  a.set(Math.round(cx), Math.round(cy) - 12, '#e8fbff')

  // weed and root growing out of him
  for (let i = -3; i <= 3; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.11)
    a.line(x, Math.round(s * (top + 0.34)), Math.round(x + i), Math.round(s * (top + 0.5)), '#2a6b57')
    a.set(Math.round(x + i), Math.round(s * (top + 0.51)), '#3ee2b4')
  }
})

registerRecipe('cistern_king', { key: 'cistern_king', size: 64, params: { stand: 0 } })
registerRecipe('cistern_king_stand', { key: 'cistern_king', size: 64, params: { stand: 1 } })

// ---------------------------------------------------------------- registry

registerBossArena(D.id, ZONE2_BOSS_ARENA)
registerBoss(D.id, () => new CisternKing())
